import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { useEffect, useState, type ReactNode } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import Index from '@/pages/Index';
import Cases from '@/pages/Cases';
import Blog from '@/pages/Blog';
import BlogPost from '@/pages/BlogPost';
import AdminLogin from '@/pages/AdminLogin';
import Booking from '@/pages/Booking';
import BookAppointment from '@/pages/BookAppointment';
import Appointment from '@/pages/Appointment';
import NotFound from '@/pages/NotFound';
import { useAuth } from '@/contexts/AuthContext';
import { HelmetProvider } from 'react-helmet-async';
import Header from '@/components/Header';
import Home from '@/pages/Home';
import About from '@/pages/About';
import Services from '@/pages/Services';
import { WebsiteSettings } from '@/lib/firebase';
import { useLocation } from 'react-router-dom';
import ProtectedRoute from '@/components/PrivateRoute';
import AdminLayout from '@/layouts/AdminLayout';

// Admin Pages
import Dashboard from '@/pages/admin/Dashboard';
import Settings from '@/pages/admin/Settings';
import AdminCases from '@/pages/admin/Cases';
import Examinations from '@/pages/admin/Examinations';
import AdminAppointments from '@/pages/admin/Appointments';
import ClinicAppointments from '@/pages/admin/ClinicAppointments';
import AdminBookings from '@/pages/admin/Bookings';
import PatientProfile from '@/pages/admin/PatientProfile';
import AdminBlog from '@/pages/admin/Blog';

// Redirect if already logged in
const RedirectIfAuthenticated = ({ children }: { children: ReactNode }) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Only redirect if on /admin/login
  if (currentUser && location.pathname === '/admin/login') {
    return <Navigate to="/admin" replace />;
  }

  return <>{children}</>;
};

function App() {
  const [settings, setSettings] = useState<WebsiteSettings | null>(null);
  const { currentUser, loading } = useAuth();
  const [mainPaddingTop, setMainPaddingTop] = useState(0);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'website');
        const settingsDoc = await getDoc(settingsRef);
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          console.log('Settings loaded:', data); // Debug log
          setSettings(data as WebsiteSettings);
        } else {
          console.log('No settings document found');
          // Set default settings if none exist
          setSettings({
            doctor: {
              name: '',
              title: '',
              image: '',
              about: '',
              qualifications: [],
              statistics: {
                experienceYears: 0,
                successfulCases: 0,
                satisfactionRate: 0
              }
            },
            clinic: {
              name: '',
              logo: '',
              headerImage: '',
              pageTitle: '',
              phone: [],
              address: [],
              workingHours: [],
              clinicPhotos: [],
              images: [],
              socialMedia: {
                facebook: '',
                instagram: '',
                twitter: '',
                whatsapp: ''
              },
              contactInfo: {
                email: '',
                whatsapp: '',
                mapEmbed: ''
              },
              location: {
                mapEmbed: ''
              }
            },
            qualifications: []
          });
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    // حساب ارتفاع الهيدر و info bar إذا لم تكن صفحة admin
    if (!window.location.pathname.startsWith('/admin')) {
      setTimeout(() => {
        const header = document.querySelector('header');
        const infoBar = document.getElementById('top-info-bar');
        let total = 0;
        if (infoBar) total += infoBar.offsetHeight;
        if (header) total += header.offsetHeight;
        setMainPaddingTop(total);
      }, 100); // تأخير بسيط لضمان ظهور العناصر
    } else {
      setMainPaddingTop(0);
    }
  }, [settings]);

  // Only show loading spinner when both loading and settings are not available
  if (loading && !settings) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <Router>
        <div className="min-h-screen flex flex-col">
          {!window.location.pathname.startsWith('/admin') && <Header settings={settings} />}
          <main className="flex-grow" style={{ paddingTop: mainPaddingTop }}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/cases" element={<Cases />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:id" element={<BlogPost />} />
              <Route path="/booking" element={<Booking />} />
              <Route path="/book-appointment" element={<BookAppointment />} />
              <Route path="/appointment" element={<Appointment />} />

              {/* Admin Login Route (no protection) */}
              <Route path="/admin/login" element={
                <RedirectIfAuthenticated>
                  <AdminLogin />
                </RedirectIfAuthenticated>
              } />

              {/* Admin Protected Routes with Layout */}
              <Route path="/admin" element={<ProtectedRoute />}>
                <Route element={<AdminLayout />}>
                  <Route index element={<Dashboard />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="cases" element={<AdminCases />} />
                  <Route path="examinations" element={<Examinations />} />
                  <Route path="appointments" element={<AdminAppointments />} />
                  <Route path="clinic-appointments" element={<ClinicAppointments />} />
                  <Route path="bookings" element={<AdminBookings />} />
                  <Route path="patient-profile" element={<PatientProfile />} />
                  <Route path="blog" element={<AdminBlog />} />
                </Route>
              </Route>

              {/* Not Found Route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
        </div>
        <Toaster />
      </Router>
    </HelmetProvider>
  );
}

export default App;
